import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { COMPANY_PHONE_NUMBER } from 'src/app/constants/app.constants';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { ACCOUNT_TYPE_PREVIEW_ROUTE } from 'src/app/constants/route.constants';
import { Alert } from 'src/app/interfaces/alert';
import { User } from 'src/app/interfaces/user';
import { AuthParams, UserAuthenticationType, AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { RedirectionParams, RedirectionService } from 'src/app/services/redirection/redirection.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';
import { RegistrationProgressService } from 'src/app/services/registration-progress/registration-progress.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-tok-login',
  templateUrl: './tok-login.component.html',
  styleUrls: ['./tok-login.component.scss']
})
export class TokLoginComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectTimer = 0;
  redirectParams?: RedirectionParams;
  hidePassword: boolean = true;
  authWithChipNumber: boolean = false;
  isBrowser: boolean;

  model: AuthParams = {
    emailAddress: '',
    password: '',
    authenticationType: UserAuthenticationType.Email,
  };

  constructor(
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private userService: UserService,
    private registrationProgressService: RegistrationProgressService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }
  
  ngOnInit(): void {
    if (this.authManager.isAuthenticated) {
      this.userService.getMe().subscribe((userResponse: User) => {
        this.redirectUserOrNavigateToDashboard();
      });
    }

    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the signup page - 409 status code)
    if (this.route.snapshot.queryParams.email) {
      this.model.emailAddress = this.route.snapshot.queryParams.email;
      this.alert = {
        type: 'success',
        message: `It looks like you're already registered. Please enter your password to log in.`,
        bold: true,
      };
    }

    // Setting redirection to be used when after login we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  login() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('login::clicked');
    this.model.authenticationType = UserAuthenticationType.Email;
    this.authManager
      .login(this.model)
      .subscribe(
        (user) => {
          if (!user) {
            this.alert = {
              type: 'danger',
              message: 'You do not have permission to access this application.',
            };
            return;
          }
          // if the user comes from the account-type-preview page we want to redirect him to the dashboard
          if (
            this.redirectParams?.l &&
            !this.redirectParams.l.includes(`/${ACCOUNT_TYPE_PREVIEW_ROUTE}`)
          ) {
            this.router.navigate(this.redirectParams.l, {
              queryParams: this.redirectParams.q,
              fragment: this.redirectParams.f,
            });
          } else {
            this.redirectUserOrNavigateToDashboard();
          }
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            if (
              err.error &&
              err.error.error_code === 'multiple_users_with_phone'
            ) {
              this.alert = {
                type: 'danger',
                message: err.error.error_message,
              };
            } else {
              this.alert = {
                type: 'danger',
                message:
                  'Please enter a valid email address, phone number or chip number and a password.',
              };
            }
          } else if (err instanceof HttpErrorResponse && err.status === 401) {
             if (err.error && err.error.error_code === 'password_not_set') {
              // Legacy user with password not set
              this.router.navigate(['/forgot-password'], {
                queryParams: {
                  email: this.model.emailAddress,
                  type: 'passnotset',
                },
              });
            } else if (err.error && err.error.error_code === 'inactive_user') {
              this.alert = {
                type: 'danger',
                message: `Please contact identibase on ${COMPANY_PHONE_NUMBER}.`,
              };
            } else {
              this.alert = {
                type: 'danger',
                message: 'The email address or password is incorrect.',
              };
            }
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }
  private redirectUserOrNavigateToDashboard() {
    this.router.navigate(['/tok-details']);
  }
  
  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  displayAlert(alertText: string) {
    this.alert = {
      type: 'danger',
      message: alertText,
    };
  }
  closeAlert() {
    this.alert = null;
  }

}
